/**
 * InterestForm Component
 * 
 * Modal form for registering interest in presential courses with minimum quota.
 * Captures personal information and submits to the course interest API.
 * Includes anti-spam measures: honeypot, reCAPTCHA, minimum form time.
 * 
 * Requirements: 1.1-1.5, 2.1-2.7, 10.1, 10.6-10.12, 15.1-15.5
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  CreditCard,
  X,
  Loader2,
  Send,
  CheckCircle,
  AlertCircle,
  Users
} from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { validateRut, formatRut } from '../utils/rutValidation';
import { validateChileanPhone, formatChileanPhone } from '../utils/phoneValidation';

// Constants
const MIN_FORM_TIME_MS = 3000; // 3 seconds minimum form time
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3001';

/**
 * Validates email format
 */
function validateEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Validates all required form fields
 */
function validateFormFields(formData, recaptchaToken = '') {
  const errors = {};

  if (!formData.nombre || formData.nombre.trim() === '') {
    errors.nombre = 'El nombre es requerido';
  }

  if (!formData.email || formData.email.trim() === '') {
    errors.email = 'El email es requerido';
  } else if (!validateEmail(formData.email)) {
    errors.email = 'Ingresa un email válido';
  }

  if (!formData.rut || formData.rut.trim() === '') {
    errors.rut = 'El RUT es requerido';
  } else {
    const rutResult = validateRut(formData.rut);
    if (!rutResult.isValid) {
      errors.rut = rutResult.error;
    }
  }

  if (!formData.telefono || formData.telefono.trim() === '') {
    errors.telefono = 'El teléfono es requerido';
  } else {
    const phoneToValidate = formData.telefono.startsWith('+56') || formData.telefono.startsWith('56')
      ? formData.telefono
      : `+56${formData.telefono.replace(/\s/g, '')}`;

    const phoneResult = validateChileanPhone(phoneToValidate);
    if (!phoneResult.isValid) {
      errors.telefono = phoneResult.error || 'Ingresa un número chileno válido (+56 9 XXXX XXXX)';
    }
  }

  if (!formData.consent) {
    errors.consent = 'Debes aceptar el procesamiento de tus datos para continuar';
  }

  if (RECAPTCHA_SITE_KEY && (!recaptchaToken || recaptchaToken.trim() === '')) {
    errors.recaptcha = 'Por favor, completa el captcha';
  }

  return errors;
}

/**
 * Submit interest to API
 */
async function submitInterest(data) {
  const response = await fetch(`${PAYMENT_API_URL}/course-interest`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    const error = new Error(result.error || result.errors?.[0] || 'Error al registrar interés');
    error.status = response.status;
    error.data = result;
    throw error;
  }

  return result;
}

/**
 * InterestForm Modal Component
 * 
 * @param {Object} props
 * @param {number} props.courseId - Course ID
 * @param {string} props.courseName - Course name to display
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} props.onSuccess - Callback when submission succeeds
 */
export default function InterestForm({
  courseId,
  courseName,
  isOpen,
  onClose,
  onSuccess,
}) {
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    rut: '',
    telefono: '',
    consent: false,
    honeypot: '',
  });

  // reCAPTCHA state and ref
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);

  // Track when form was opened for anti-spam
  const formOpenedAt = useRef(Date.now());

  // UI state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      formOpenedAt.current = Date.now();
      setFormData({
        nombre: '',
        email: '',
        rut: '',
        telefono: '',
        consent: false,
        honeypot: '',
      });
      setRecaptchaToken('');
      setErrors({});
      setSubmitError(null);
      setSubmitSuccess(false);
      setIsDuplicate(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRutBlur = () => {
    if (formData.rut) {
      const formatted = formatRut(formData.rut);
      if (formatted) {
        setFormData(prev => ({ ...prev, rut: formatted }));
      }
    }
  };

  const handlePhoneBlur = () => {
    if (formData.telefono && formData.telefono.trim() !== '') {
      const phoneToValidate = formData.telefono.startsWith('+56') || formData.telefono.startsWith('56')
        ? formData.telefono
        : `+56${formData.telefono.replace(/\s/g, '')}`;

      const result = validateChileanPhone(phoneToValidate);
      if (result.isValid && result.formatted) {
        const withoutPrefix = result.formatted.replace(/^\+56\s*/, '');
        setFormData(prev => ({ ...prev, telefono: withoutPrefix }));
        if (errors.telefono) {
          setErrors(prev => ({ ...prev, telefono: undefined }));
        }
      } else if (!result.isValid) {
        setErrors(prev => ({ ...prev, telefono: result.error }));
      }
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token || '');
    if (token && errors.recaptcha) {
      setErrors(prev => ({ ...prev, recaptcha: undefined }));
    }
  };

  const handleRecaptchaExpire = () => {
    setRecaptchaToken('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Anti-spam: Check honeypot field (silent success)
    if (formData.honeypot) {
      setSubmitSuccess(true);
      return;
    }

    // Anti-spam: Check minimum time
    const timeElapsed = Date.now() - formOpenedAt.current;
    if (timeElapsed < MIN_FORM_TIME_MS) {
      setSubmitError('Por favor, completa el formulario correctamente');
      return;
    }

    // Validate all fields
    const validationErrors = validateFormFields(formData, recaptchaToken);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Format data for API
      const rutResult = validateRut(formData.rut);
      const phoneToValidate = formData.telefono.startsWith('+56') || formData.telefono.startsWith('56')
        ? formData.telefono
        : `+56${formData.telefono.replace(/\s/g, '')}`;
      const phoneResult = validateChileanPhone(phoneToValidate);

      const submissionData = {
        courseId,
        courseName,
        nombre: formData.nombre.trim(),
        email: formData.email.trim().toLowerCase(),
        rut: rutResult.formatted,
        telefono: phoneResult.formatted,
        consent: formData.consent,
        recaptchaToken,
        formOpenedAt: formOpenedAt.current,
        honeypot: formData.honeypot,
      };

      const result = await submitInterest(submissionData);

      if (result.success) {
        setSubmitSuccess(true);
        if (onSuccess) {
          onSuccess(result);
        }
      }
    } catch (error) {
      console.error('Interest submission failed:', error);

      if (error.status === 409 && error.data?.isDuplicate) {
        setIsDuplicate(true);
        setSubmitSuccess(true);
      } else {
        setSubmitError(error.message || 'Error al registrar tu interés. Por favor, intenta de nuevo.');
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken('');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <div
              className="relative w-full max-w-lg bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
              role="dialog"
              aria-modal="true"
              aria-labelledby="interest-form-title"
            >
              {/* Close button */}
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white 
                           hover:bg-white/10 rounded-lg transition-colors z-10
                           disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Cerrar formulario"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="p-6 sm:p-8">
                {submitSuccess ? (
                  /* Success State */
                  <SuccessMessage
                    isDuplicate={isDuplicate}
                    onClose={handleClose}
                  />
                ) : (
                  /* Form */
                  <>
                    {/* Header */}
                    <div className="text-center mb-6">
                      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 
                                      flex items-center justify-center">
                        <Users className="w-7 h-7 text-white" />
                      </div>
                      <h2
                        id="interest-form-title"
                        className="text-2xl font-bold text-white mb-2"
                      >
                        Registrar Interés
                      </h2>
                      <p className="text-lg text-cyan-400 font-medium mb-2">
                        {courseName}
                      </p>
                      {/* Requirement 1.4: Minimum quota message */}
                      <p className="text-sm text-slate-400">
                        Este curso requiere un mínimo de 4 alumnos para iniciar.
                        Te contactaremos cuando se complete el grupo.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      {/* Nombre */}
                      <FormField
                        id="nombre"
                        name="nombre"
                        label="Nombre Completo"
                        type="text"
                        value={formData.nombre}
                        onChange={handleInputChange}
                        error={errors.nombre}
                        icon={User}
                        placeholder="Tu nombre completo"
                        required
                        autoComplete="name"
                      />

                      {/* Email */}
                      <FormField
                        id="email"
                        name="email"
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        error={errors.email}
                        icon={Mail}
                        placeholder="tu@email.com"
                        required
                        autoComplete="email"
                        inputMode="email"
                      />

                      {/* RUT */}
                      <FormField
                        id="rut"
                        name="rut"
                        label="RUT"
                        type="text"
                        value={formData.rut}
                        onChange={handleInputChange}
                        onBlur={handleRutBlur}
                        error={errors.rut}
                        icon={CreditCard}
                        placeholder="12.345.678-9"
                        required
                        inputMode="text"
                      />

                      {/* Teléfono */}
                      <div>
                        <label
                          htmlFor="telefono"
                          className="block text-sm font-medium text-slate-300 mb-2"
                        >
                          Teléfono *
                        </label>
                        <div className="relative flex">
                          <div className="flex items-center px-3 bg-white/10 border border-r-0 border-white/10 rounded-l-xl">
                            <span className="text-slate-400 font-medium select-none text-sm">+56</span>
                          </div>
                          <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Phone className="w-4 h-4 text-slate-500" aria-hidden="true" />
                            </div>
                            <input
                              type="tel"
                              id="telefono"
                              name="telefono"
                              value={formData.telefono}
                              onChange={handleInputChange}
                              onBlur={handlePhoneBlur}
                              placeholder="9 1234 5678"
                              aria-label="Número de teléfono"
                              aria-required="true"
                              aria-invalid={!!errors.telefono}
                              aria-describedby={errors.telefono ? 'telefono-error' : undefined}
                              autoComplete="tel"
                              inputMode="tel"
                              className={`
                                w-full pl-10 pr-4 py-2.5 bg-white/5 border rounded-r-xl
                                text-white placeholder-slate-500 text-sm
                                focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                                transition-all duration-200
                                ${errors.telefono ? 'border-red-500/50' : 'border-white/10 hover:border-white/20'}
                              `}
                            />
                          </div>
                        </div>
                        {errors.telefono && (
                          <p id="telefono-error" className="mt-1.5 text-xs text-red-400" role="alert">
                            {errors.telefono}
                          </p>
                        )}
                      </div>

                      {/* Consent Checkbox - Requirement 13.1 */}
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="consent"
                          name="consent"
                          checked={formData.consent}
                          onChange={handleInputChange}
                          aria-invalid={!!errors.consent}
                          aria-describedby={errors.consent ? 'consent-error' : undefined}
                          className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 
                                     text-blue-500 focus:ring-blue-500/50 focus:ring-offset-0
                                     cursor-pointer"
                        />
                        <label htmlFor="consent" className="text-sm text-slate-400 cursor-pointer">
                          Acepto que mis datos sean utilizados para contactarme sobre este curso *
                        </label>
                      </div>
                      {errors.consent && (
                        <p id="consent-error" className="text-xs text-red-400 -mt-2" role="alert">
                          {errors.consent}
                        </p>
                      )}

                      {/* Honeypot field - hidden from humans (Requirement 10.1) */}
                      <div className="absolute -left-[9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input
                          type="text"
                          id="website"
                          name="honeypot"
                          value={formData.honeypot}
                          onChange={handleInputChange}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </div>

                      {/* Submit Error */}
                      {submitError && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-2"
                        >
                          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <p className="text-red-400 text-sm">{submitError}</p>
                        </motion.div>
                      )}

                      {/* reCAPTCHA - Requirement 10.6, 10.7 */}
                      {RECAPTCHA_SITE_KEY && (
                        <div className="flex flex-col items-center">
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={RECAPTCHA_SITE_KEY}
                            onChange={handleRecaptchaChange}
                            onExpired={handleRecaptchaExpire}
                            theme="dark"
                            aria-label="Verificación de seguridad reCAPTCHA"
                          />
                          {errors.recaptcha && (
                            <p className="mt-2 text-xs text-red-400" role="alert">
                              {errors.recaptcha}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Submit Button */}
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                        aria-label={isSubmitting ? 'Enviando...' : 'Registrar mi interés'}
                        aria-busy={isSubmitting}
                        className={`
                          w-full py-3 rounded-xl font-semibold text-base
                          flex items-center justify-center gap-2
                          transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:ring-offset-slate-900
                          ${isSubmitting
                            ? 'bg-slate-600 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/30'
                          }
                          text-white
                        `}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                            Registrando...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" aria-hidden="true" />
                            Registrar mi Interés
                          </>
                        )}
                      </motion.button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * Reusable form field component
 */
function FormField({
  id,
  name,
  label,
  type,
  value,
  onChange,
  onBlur,
  error,
  icon: Icon,
  placeholder,
  required,
  autoComplete,
  inputMode,
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-300 mb-2">
        {label} {required && '*'}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="w-4 h-4 text-slate-500" aria-hidden="true" />
        </div>
        <input
          type={type}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          aria-label={label}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          autoComplete={autoComplete}
          inputMode={inputMode}
          className={`
            w-full pl-10 pr-4 py-2.5 bg-white/5 border rounded-xl
            text-white placeholder-slate-500 text-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
            transition-all duration-200
            ${error ? 'border-red-500/50' : 'border-white/10 hover:border-white/20'}
          `}
        />
      </div>
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-xs text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Success message component
 */
function SuccessMessage({ isDuplicate, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-6"
    >
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 
                      flex items-center justify-center">
        <CheckCircle className="w-8 h-8 text-white" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2">
        {isDuplicate ? '¡Ya estás registrado!' : '¡Gracias por tu interés!'}
      </h3>
      <p className="text-slate-400 mb-6 max-w-sm mx-auto">
        {isDuplicate
          ? 'Ya registramos tu interés en este curso anteriormente. Te contactaremos cuando se complete el grupo.'
          : 'Te contactaremos cuando se complete el grupo de 4 alumnos para iniciar el curso.'
        }
      </p>
      <motion.button
        type="button"
        onClick={onClose}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 
                   hover:from-blue-600 hover:to-cyan-600 text-white font-semibold 
                   rounded-xl shadow-lg shadow-blue-500/30 transition-all"
      >
        Cerrar
      </motion.button>
    </motion.div>
  );
}
